import { classes } from "./classes.js";
import {
  BANK_BAD_BORDER,
  BANK_BG_SURFACE,
  BANK_BORDER_STRONG,
  BANK_FOCUS,
  BANK_FS_READ,
  BANK_RADIUS_CTL,
  BANK_TEXT,
} from "./tokens.js";

/**
 * `F-0b`: the bank's text field (prototype `.input`): 42 px minimum, 15 px, a visible border and a 3 px focus ring.
 *
 * It hands back the VALUE, not the event, like `Select`. `Field` stays the label/hint/error wrapper: the caller
 * passes the hint's or error's id as `describedBy` and `invalid` when there is an error, so a screen reader
 * hears the error with the field.
 */
export interface InputProps {
  readonly id: string;
  readonly value: string;
  readonly onChange?: (value: string) => void;
  readonly type?: "text" | "email" | "password" | "number" | "date" | "tel" | "search";
  readonly placeholder?: string;
  readonly invalid?: boolean;
  readonly describedBy?: string;
  readonly readOnly?: boolean;
  readonly required?: boolean;
  readonly autoComplete?: string;
  readonly inputMode?: "text" | "numeric" | "decimal" | "email" | "tel" | "search";
  readonly className?: string;
  readonly testId?: string;
}

export const BANK_FIELD_CLASSES = classes(
  "block w-full min-h-[42px] border px-3 font-medium",
  BANK_RADIUS_CTL,
  BANK_FS_READ,
  BANK_BG_SURFACE,
  BANK_TEXT,
  BANK_FOCUS,
);

export function Input(props: InputProps): React.JSX.Element {
  const { id, value, onChange, type = "text", invalid, className } = props;
  return (
    <input
      id={id}
      type={type}
      value={value}
      onChange={(event) => onChange?.(event.target.value)}
      placeholder={props.placeholder}
      readOnly={props.readOnly}
      required={props.required}
      autoComplete={props.autoComplete}
      inputMode={props.inputMode}
      aria-invalid={invalid === true ? true : undefined}
      aria-describedby={props.describedBy}
      data-kit="input"
      data-testid={props.testId}
      className={classes(
        BANK_FIELD_CLASSES,
        invalid === true ? BANK_BAD_BORDER : BANK_BORDER_STRONG,
        className,
      )}
    />
  );
}
